/**
 * App shell (redesign v2, 2026-10-17): chapda 72px ikon panel (nom hover'da, qadab kengaytiriladi), tepada hamma rol
 * uchun bir xil panel -- qidiruv (⌘K), widgetlar, bildirishnoma, profil. Tungi rejim va til profil menyusi ichida.
 * Filial tanlovi bu yerda emas, sahifa sarlavhasida (components/ScopePicker). Menyu -- app/nav.ts (rolga qarab).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Bell, ChartColumn, ChevronDown, LogOut, PanelLeftClose, PanelLeftOpen, Search, User as UserIcon, WifiOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { startRealtime, stopRealtime, useConnectionState } from "@/lib/realtime";
import { useLogout } from "@/features/auth/hooks";
import type { Me } from "@/features/auth/types";
import { useProfileLanguage } from "@/features/auth/language";
import { useIntroPhase } from "@/features/auth/intro";
import { useWidgetRail, WidgetDock, WidgetRail } from "@/features/widgets/WidgetDock";
import { Avatar, SectionLabel } from "@/components/ui/misc";
import { CountBadge } from "@/components/ui/badge";
import { BrandIcon, BrandLockup } from "@/components/brand/BrandLogo";
import { AppearanceSwitcher } from "@/components/ThemeToggle";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CommandPalette } from "@/components/CommandPalette";
import { isNavActive, menuFor, type Counts } from "./nav";

const EMPTY_COUNTS: Counts = { approvals: 0, notifications: 0, tasks_overdue: 0 };

export function AppShell({ me }: { me: Me }) {
  const location = useLocation();
  const [pinned, setPinned] = useRailPinned();
  const { t } = useTranslation();
  // Profil tili: kirish, boshqa oyna/qurilmadagi o'zgarish. Bosilgan tilni qaytarib qo'ymaydi.
  useProfileLanguage(me.language);
  const logout = useLogout();
  const conn = useConnectionState();
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Login intro'dan keyin platforma effekt bilan ochiladi (components/brand/login-intro.css: .app-reveal)
  const introPhase = useIntroPhase();
  // Widget paneli o'ngga qadalgan bo'lsa -- kontent yonida ustun (features/widgets)
  const widgetRail = useWidgetRail(me);
  const navigate = useNavigate();
  // Orqaga (2026-11-03): ilova ichida oldingi sahifa bo'lsa (router tarixi `idx`); bo'lmasa tugma o'chiq -- joyi o'zgarmaydi
  const canGoBack = ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;

  // Real-time guruhlari ulanishda doiradan olinadi: admin boshqa filial biriktirsa -- qayta ulanadi va
  // ro'yxatlar yangi doira bo'yicha qayta so'raladi
  const scopeKey = [me.role, me.branch?.id, me.scope_branches.map((b) => b.id)].join("|");
  const connectedScope = useRef(scopeKey);
  useEffect(() => {
    startRealtime();
    if (connectedScope.current !== scopeKey) {
      connectedScope.current = scopeKey;
      void queryClient.invalidateQueries();
    }
    return () => stopRealtime();
  }, [scopeKey]);

  // ⌘K / Ctrl+K -- buyruq paneli (matn maydonida ham ishlaydi: bu global qisqa yo'l)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const counts = useQuery({
    queryKey: ["counts"],
    queryFn: () => api.get<Counts>("/api/auth/counts/"),
    refetchInterval: 60_000,
  });
  const c: Counts = counts.data ?? EMPTY_COUNTS;
  const menu = menuFor(me);

  // Menyu ekran bo'yiga sig'masa (telefon yotiq holatda) -- panel qotirilmaydi, sahifa bilan birga aylanadi; sig'sa (noutbuk)
  // joyida qotib turadi va sahifa aylanganda qimirlamaydi (2026-11-03)
  const railRef = useRef<HTMLElement>(null);
  const [railFlows, setRailFlows] = useState(false);
  useLayoutEffect(() => {
    const check = () => {
      const el = railRef.current;
      if (!el) return;
      const natural = [...el.children].reduce((n, child) => n + (child as HTMLElement).offsetHeight, 0);
      setRailFlows(natural > window.innerHeight);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [pinned, menu.length]);

  // Balandlik kontentga teng (h-full emas): aks holda yopishqoq panelning konteyneri ekran bo'yi bo'lib, sticky ishlamaydi
  return (
    <div className={cn("flex min-h-screen", introPhase === "reveal" && "app-reveal")}>
      <aside
        id="app-rail"
        ref={railRef}
        className={cn(
          "rail z-40 flex shrink-0 flex-col border-r border-line bg-sidebar/80 backdrop-blur-xl",
          // Oqimda -- sahifa bo'yi cho'ziladi (fon uzilmaydi), menyu tepada; qotirilganda -- ekran bo'yi
          railFlows ? "relative self-stretch" : "sticky top-0 h-screen self-start",
          pinned ? "w-[220px]" : "w-[72px]",
        )}
        aria-label={t("nav.expand")}
      >
        <div className={cn("flex items-center pt-4 pb-4", pinned ? "px-4" : "justify-center")}>
          <NavLink to="/" className="rounded-xl" aria-label={t("app")}>
            {pinned ? <BrandLockup height={26} /> : <BrandIcon size={36} />}
          </NavLink>
        </div>
        <nav className={cn("flex flex-col gap-1", pinned ? "px-3" : "items-center px-3.5")}>
          {menu.map((item) => {
            const badge = item.badge ? item.badge(c) : 0;
            const label = t(`nav.${item.label}`);
            const active = isNavActive(item, location.pathname);
            return (
              <NavLink
                key={item.label}
                to={item.to}
                aria-label={!pinned ? (badge > 0 ? `${label} (${badge})` : label) : undefined}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "group relative flex items-center rounded-xl text-[14px] font-medium transition-[background-color,color] duration-150",
                  pinned ? "h-10 gap-2.5 px-3" : "h-11 w-11 justify-center",
                  active ? "bg-ink text-surface" : "text-ink-soft hover:bg-fill hover:text-ink",
                )}
              >
                {/* Faol bo'lim belgisi: panelning chap chetida sariq chiziq */}
                <span aria-hidden className={cn("absolute top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-accent transition-opacity", pinned ? "-left-3" : "-left-3.5", active ? "opacity-100" : "opacity-0")} />
                <span className="relative shrink-0">
                  <item.icon size={pinned ? 18 : 20} strokeWidth={1.75} aria-hidden />
                  {!pinned && badge > 0 && (
                    <CountBadge count={badge} tone={item.badgeTone ?? "grey"} className="absolute -top-2 -right-3 ml-0 h-4 min-w-[16px] px-1 text-[9px] ring-2 ring-sidebar" />
                  )}
                </span>
                {pinned && <span className="truncate">{label}</span>}
                {pinned && badge > 0 && <CountBadge count={badge} tone={item.badgeTone ?? "grey"} className={cn(active && item.badgeTone !== "red" && "bg-surface text-ink")} />}
                {!pinned && (
                  // Yig'ilgan holatda nom: sichqoncha yoki klaviatura fokusida chiqadi (120 ms)
                  <span
                    aria-hidden
                    className="tip-in pointer-events-none absolute top-1/2 left-full z-50 ml-3 hidden -translate-y-1/2 rounded-lg bg-ink px-2.5 py-1.5 text-[12px] font-semibold whitespace-nowrap text-surface shadow-menu group-hover:block group-focus-visible:block"
                  >
                    {label}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className={cn("mt-auto flex flex-col gap-2 pb-4", pinned ? "px-3" : "items-center px-3.5")}>
          {pinned && (
            <div className="truncate px-3 text-[11px] text-muted">
              {me.branch ? me.branch.name : t("allBranches")}
            </div>
          )}
          <button
            type="button"
            onClick={() => setPinned(!pinned)}
            aria-controls="app-rail"
            aria-expanded={pinned}
            aria-label={pinned ? t("nav.unpin") : t("nav.pin")}
            title={pinned ? t("nav.unpin") : t("nav.pin")}
            className={cn("flex items-center rounded-xl text-faint transition-colors hover:bg-fill hover:text-ink", pinned ? "h-9 gap-2.5 px-3 text-[12px]" : "h-10 w-10 justify-center")}
          >
            {pinned ? <PanelLeftClose size={16} strokeWidth={1.75} aria-hidden /> : <PanelLeftOpen size={18} strokeWidth={1.75} aria-hidden />}
            {pinned && <span>{t("nav.unpin")}</span>}
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-line bg-canvas/85 px-5 backdrop-blur-xl">
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={!canGoBack}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-fill text-ink transition-colors hover:bg-fill-hover disabled:cursor-default disabled:text-faint disabled:hover:bg-fill"
            aria-label={t("actions.back")}
            title={t("actions.back")}
          >
            <ArrowLeft size={17} strokeWidth={1.75} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="flex h-10 w-full max-w-[460px] min-w-[120px] items-center gap-2.5 rounded-control bg-fill px-3.5 text-left text-[13px] text-muted transition-colors hover:bg-fill-hover"
            aria-label={t("palette.searchButton")}
            aria-keyshortcuts="Meta+K Control+K"
          >
            <Search size={15} aria-hidden />
            <span className="flex-1 truncate">{t("palette.placeholder")}</span>
            <kbd className="hidden rounded-md border border-border bg-surface px-1.5 py-0.5 text-[10.5px] font-semibold text-muted sm:block">⌘K</kbd>
          </button>
          <div className="ml-auto flex items-center gap-2">
            {conn !== "online" && (
              <span className="inline-flex h-8 items-center gap-1.5 rounded-full bg-danger-soft px-3 text-[11px] font-semibold text-danger" title={t("liveUpdatesPaused")}>
                <WifiOff size={12} /> {conn === "connecting" ? t("reconnecting") : t("offline")}
              </span>
            )}
            <WidgetDock me={me} />
            <NavLink
              to="/notifications"
              className={({ isActive }) => cn("relative grid h-10 w-10 place-items-center rounded-full bg-fill text-ink transition-colors hover:bg-fill-hover", isActive && "bg-ink text-surface hover:bg-ink")}
              aria-label={c.notifications > 0 ? `${t("nav.notifications")} (${c.notifications})` : t("nav.notifications")}
              title={t("nav.notifications")}
            >
              <Bell size={17} strokeWidth={1.75} aria-hidden />
              {c.notifications > 0 && <span className="live-dot absolute top-2 right-2 h-2 w-2 rounded-full bg-accent ring-2 ring-fill" aria-hidden />}
            </NavLink>
            <UserMenu me={me} onLogout={() => logout.mutate()} />
          </div>
        </header>
        <div className="flex flex-1">
          <main className="min-w-0 flex-1 overflow-x-hidden px-6 py-5">
            <div key={location.pathname} className="page-enter">
              <Outlet />
            </div>
          </main>
          {widgetRail && <WidgetRail me={me} />}
        </div>
      </div>
      <CommandPalette me={me} open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

const RAIL_KEY = "dh.rail.pinned";

/** Ikon panel qadalganmi (kengaytirilgan): shu qurilmada eslab qolinadi, boshqa tablar ergashadi */
function useRailPinned(): [boolean, (value: boolean) => void] {
  const [pinned, setState] = useState(readPinned);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === RAIL_KEY) setState(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const set = useCallback((value: boolean) => {
    setState(value);
    try {
      localStorage.setItem(RAIL_KEY, value ? "1" : "0");
    } catch {
      /* ixtiyoriy: maxfiy rejim */
    }
  }, []);
  return [pinned, set];
}

function readPinned(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === "1";
  } catch {
    return false;
  }
}

/** Profil menyusi: mening sahifam, profil va xavfsizlik, ko'rinish (kun/tun/avto), til, chiqish */
function UserMenu({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };
  const scope = [t(`roles.${me.role}`), me.branch?.name ?? (me.role === "owner" ? t("allBranches") : null)].filter(Boolean).join(" · ");

  return (
    <div ref={wrap} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn("flex h-10 items-center gap-2 rounded-full bg-fill pr-2.5 pl-1 text-left transition-colors hover:bg-fill-hover", open && "bg-raised shadow-pill")}
      >
        <Avatar initials={me.initials} name={me.full_name} size={32} tone="ink" />
        <span className="hidden flex-col leading-tight md:flex">
          <span className="text-[13px] font-semibold">{me.display_name}</span>
          <span className="text-[11px] text-muted">{t(`roles.${me.role}`)}</span>
        </span>
        <ChevronDown size={14} className={cn("text-muted transition-transform duration-200", open && "rotate-180")} aria-hidden />
      </button>
      {open && (
        <div role="menu" aria-label={me.full_name} className="pop-in absolute right-0 z-50 mt-2 w-[292px] rounded-card border border-border/60 bg-surface p-2 shadow-menu">
          <div className="flex items-center gap-3 px-2.5 pt-2 pb-3">
            <Avatar initials={me.initials} name={me.full_name} size={40} tone="ink" />
            <div className="min-w-0 leading-tight">
              <div className="truncate text-[15px] font-semibold">{me.full_name}</div>
              <div className="truncate text-[12px] text-muted">{scope}</div>
            </div>
          </div>
          <MenuRow icon={ChartColumn} onClick={() => go(`/people/${me.id}`)}>
            {t("nav.myPage")}
          </MenuRow>
          <MenuRow icon={UserIcon} onClick={() => go("/profile")}>
            {t("nav.profileSecurity")}
          </MenuRow>
          <div className="mx-1 my-2 h-px bg-line" />
          <SectionLabel className="px-2.5 pb-1.5">{t("appearance.title")}</SectionLabel>
          <div className="px-1.5 pb-2">
            <AppearanceSwitcher fullWidth />
          </div>
          <SectionLabel className="px-2.5 pb-1.5">{t("language")}</SectionLabel>
          <div className="px-1.5 pb-1">
            <LanguageSwitcher fullWidth />
          </div>
          <div className="mx-1 my-2 h-px bg-line" />
          <MenuRow icon={LogOut} onClick={onLogout} danger>
            {t("nav.logout")}
          </MenuRow>
        </div>
      )}
    </div>
  );
}

function MenuRow({ icon: Icon, onClick, children, danger }: { icon: typeof LogOut; onClick: () => void; children: ReactNode; danger?: boolean }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn("flex h-10 w-full items-center gap-2.5 rounded-xl px-2.5 text-left text-[14px] font-medium transition-colors hover:bg-fill", danger ? "text-danger" : "text-ink")}
    >
      <Icon size={16} strokeWidth={1.75} className={danger ? "text-danger" : "text-muted"} aria-hidden />
      <span className="flex-1">{children}</span>
    </button>
  );
}
